import React, { FC, useCallback, useEffect } from 'react'

import { priceToString, SIM_JOURNEY_TYPES } from '@smarty-nx/code-utils'
import { isUnlimitedPlan } from '@smarty-nx/contentful'
import { Plan } from '@smarty-nx/data'
import { Typography } from '@smarty-nx/ds'
import {
  CHECKOUT_TYPES,
  DATA_LAYER_EVENTS,
  TRACKING_CATEGORIES,
} from '@smarty-nx/tracking'
import braintree from 'braintree-web'
import cn from 'classnames'

import ApplePayButton from './ApplePayButton'
import classes from './plan-details-banner.module.scss'
import { CONFIG, FEATURES } from '../../config'
import { TC_LINK } from '../../global.constants'
import { useIsAuthenticated } from '../../hooks'
import PlanDetailLoading from '../../static/images/planDetailLoading.svg'
import PlanDetailLoadingMob from '../../static/images/planDetailLoadingMob.svg'
import api from '../../utils/api'
import { triggerProductEvent } from '../../utils/dataLayer'
import { buildAssetsProductsProps } from '../../utils/dmpgAttributes'
import { convertDataToDisplay, pricePresenter } from '../../utils/formatters'
import { renderPaypalButton } from '../../utils/paypal'
import { checkDataAmountIsDiff, returnDataWithUnit } from '../../utils/plans'
import { PlanMeta } from '../../utils/plans.types'
import ColorButton from '../ColorButton'
import OfferRoundel from '../OfferRoundel'
import {
  HideOnMobile,
  HideOnTablet,
  HideSmallDesktopUpwards,
  HideTabletUpwards,
  Paragraph,
} from '../shared'
import TextLink from '../TextLink'

interface PlanDetailsBannerProps {
  planMeta: PlanMeta
  priceData: Plan
  buyBtnTitle?: string
  isPlanDetails?: boolean
}

export const PlanDetailsBanner: FC<PlanDetailsBannerProps> = ({
  isPlanDetails,
  planMeta,
  priceData,
  buyBtnTitle = 'Buy plan',
}) => {
  const isAuthenticated = useIsAuthenticated()

  const setupPaypal = useCallback(async () => {
    try {
      const tokenResponse = await api('/v1/orders/token')
      if (tokenResponse?.data?.data?.attributes?.token) {
        const clientInstance = await braintree.client.create({
          authorization: tokenResponse.data.data.attributes.token,
        })
        let paypalCheckoutInstance = await braintree.paypalCheckout.create({
          client: clientInstance,
        })
        paypalCheckoutInstance = await paypalCheckoutInstance.loadPayPalSDK({
          vault: true,
          commit: false,
        })

        renderPaypalButton({
          buttonSelector: '#paypal-button',
          paypalCheckoutInstance,
          displayName: CONFIG.PAYPAL_DISPLAYNAME,
          billingAgreementDescription: `${
            priceData?.name
          } SIM plan for ${pricePresenter(price)} per month`,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          locale: 'en_GB',
          plan: { planMeta, priceData },
          handleSuccessCallback: ({ payload }) => {
            const consolidatedData = {
              ...payload,
              ...data,
            }

            sessionStorage.setItem(
              'paypalData',
              JSON.stringify(consolidatedData)
            )
            const redirectUrl = `/order/${planMeta.slug}/express`
            window.location.replace(redirectUrl)
          },
          handleErrorCallback: (e) =>
            console.error('Error from paypal is ', { e }),
        })

        if (!isAuthenticated) {
          const googlePaymentInstance = await braintree.googlePayment.create({
            client: clientInstance,
            googlePayVersion: 2,
            googleMerchantId: 'merchant-id-from-google',
          })

          const paymentsClient = new window.google.payments.api.PaymentsClient({
            environment: CONFIG.GOOGLE_PAY_ENV,
          })

          paymentsClient
            .isReadyToPay({
              apiVersion: 2,
              apiVersionMinor: 0,
              allowedPaymentMethods:
                googlePaymentInstance.createPaymentDataRequest()
                  .allowedPaymentMethods,
              existingPaymentMethodRequired: true,
            })
            .then((response) => {
              if (response.result) {
                const handler = (event) => {
                  event.preventDefault()

                  const paymentDataRequest =
                    googlePaymentInstance.createPaymentDataRequest({
                      transactionInfo: {
                        currencyCode: 'GBP',
                        totalPriceStatus: 'FINAL',
                        totalPrice: event.target.getAttribute('data-price'),
                      },
                    })

                  const cardPaymentMethod =
                    paymentDataRequest.allowedPaymentMethods[0]
                  cardPaymentMethod.parameters.billingAddressRequired = true
                  cardPaymentMethod.parameters.billingAddressParameters = {
                    format: 'FULL',
                    phoneNumberRequired: true,
                  }

                  paymentsClient
                    .loadPaymentData(paymentDataRequest)
                    .then((paymentData) =>
                      googlePaymentInstance.parseResponse(paymentData)
                    )
                    .then((result) => {
                      console.log(result)
                    })
                    .catch((err) => {
                      console.error(err)
                    })
                }
                const button = document.getElementById('google-pay-button')
                if (button) {
                  button.onclick = handler
                }
              }
            })
            .catch((err) => {
              console.error(err)
            })
        }
      }
    } catch (e) {
      console.error('Error is ', { e })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, planMeta.slug, priceData?.name])

  useEffect(() => {
    if (FEATURES.PAYPAL_EXPRESS && !isAuthenticated) {
      setupPaypal()
    }
  }, [setupPaypal, isAuthenticated])

  if (!planMeta || !priceData) {
    return (
      <>
        <HideOnMobile>
          <img className="loading" src={PlanDetailLoading} alt="" />
        </HideOnMobile>
        <HideTabletUpwards>
          <img className="loading" src={PlanDetailLoadingMob} alt="" />
        </HideTabletUpwards>
      </>
    )
  }

  const isUnlimited = isUnlimitedPlan(planMeta.id)
  const isDataOnly = planMeta.plan_is_data_only
  const { voucher, groups, originalPrice, finalPriceWithoutCredit, tcChip } =
    priceData

  const defaultPrice = priceToString(originalPrice)
  const discountedPrice = priceToString(finalPriceWithoutCredit)
  const hasPriceChanged = originalPrice.value !== finalPriceWithoutCredit.value
  const price = hasPriceChanged ? discountedPrice : defaultPrice

  const isGroupDiscount = !!groups?.groupDiscountPercentage

  let planStatement
  if (planMeta.data_discount) {
    planStatement = isPlanDetails ? 'Money back for unused data' : 'Money back'
  } else {
    planStatement = 'No speed restrictions'
  }

  const defaultData = planMeta.data_number
  const [hasDataChanged, dataAmountSafe] = checkDataAmountIsDiff(
    priceData,
    planMeta
  )
  const data = priceData && convertDataToDisplay(defaultData, dataAmountSafe)

  const renderDataTypes = () => (
    <>
      5G & 4G data
      <Typography
        component="span"
        variant="Kollektif/ExtraSmall"
        className={classes.description}
      >
        5G available with enabled devices
      </Typography>
    </>
  )

  return (
    <div
      className={classes.bannerWrapper}
      id={(planMeta.name ?? '').toLowerCase()}
    >
      {isGroupDiscount && !voucher && (
        <div
          className={classes.offerWrapper}
          style={{ background: planMeta.color }}
        >
          <Typography
            variant="Kollektif/Small"
            className={classes.offerSubHeading}
          >
            10% off as a group member
          </Typography>
          <Paragraph size="12px">
            <TextLink
              newTab
              href={TC_LINK}
              ariaLabel="Full offer T&amp;Cs .pdf opens in a new window"
            >
              Full offer T&amp;Cs
            </TextLink>
          </Paragraph>
        </div>
      )}
      {voucher && (
        <>
          <div
            className={classes.offerWrapper}
            style={{ background: planMeta.color }}
          >
            <Typography
              variant="Kollektif/Small"
              className={classes.offerSubHeading}
            >
              {voucher.plan_card_heading}
            </Typography>
            <Paragraph size="12px">
              <TextLink
                newTab
                href={voucher.terms_and_conditions_url}
                ariaLabel="Full offer T&amp;Cs .pdf opens in a new window"
              >
                Full offer T&amp;Cs
              </TextLink>
            </Paragraph>
          </div>
          <HideSmallDesktopUpwards>
            <OfferRoundel
              voucher={voucher}
              roundelSide="right"
              roundelPosition="right"
              usesYellowAssets={planMeta.usesYellowAssets}
            />
          </HideSmallDesktopUpwards>
          <HideOnTablet>
            <OfferRoundel
              voucher={voucher}
              roundelSide="left"
              roundelPosition="left"
              usesYellowAssets={planMeta.usesYellowAssets}
            />
          </HideOnTablet>
        </>
      )}
      <div
        className={classes.planDetails}
        style={{
          background:
            voucher || isGroupDiscount ? planMeta.colorLight : planMeta.color,
          borderTopLeftRadius: !(voucher || isGroupDiscount)
            ? '3px'
            : undefined,
          borderTopRightRadius: !(voucher || isGroupDiscount)
            ? '3px'
            : undefined,
        }}
        data-testid="plan-details"
      >
        {tcChip && !voucher && (
          <div className={classes.chip}>
            <span>{tcChip}</span>
          </div>
        )}
        <div className={classes.pricing}>
          <Typography
            component="h2"
            variant="Kollektif/ExtraLarge"
            dataTestId="plan-price"
            className={classes.dataValue}
          >
            {priceData && price}
          </Typography>
          {(voucher || isGroupDiscount) && hasPriceChanged ? (
            <p aria-label={`Usually priced at ${defaultPrice}`}>
              <span>{defaultPrice}</span> p/m
            </p>
          ) : (
            <Typography variant="Base/Body" dataTestId="capped-text">
              Capped per month
            </Typography>
          )}
        </div>
        <div
          className={cn(classes.symbol, classes.equals, {
            [classes.hideTablet]: isDataOnly,
          })}
          data-testid="equals-sign"
        >
          =
        </div>
        <div
          className={cn(classes.includes, {
            [classes.includesDataOnly]: isDataOnly,
          })}
        >
          <Typography
            component="h2"
            variant="Kollektif/ExtraLarge"
            dataTestId="calls-and-texts-limit"
            className={classes.dataValue}
          >
            {isDataOnly ? 'Data Only' : 'Unlimited'}
          </Typography>
          {!isDataOnly && (
            <Typography variant="Base/Body" dataTestId="calls-and-texts-text">
              {isUnlimited && (
                <span className={classes.mobileCard}>5G &amp; 4G Data, </span>
              )}
              UK calls &amp; texts
            </Typography>
          )}
        </div>
        {!isUnlimited ? (
          <>
            {!isDataOnly && (
              <div
                className={cn(classes.symbol, classes.add)}
                data-testid="plus-sign"
              >
                +
              </div>
            )}
            <div className={classes.data} data-testid="data-amount">
              <div className={classes.displayData}>
                {hasDataChanged && (
                  <Typography
                    component="span"
                    variant="Kollektif/Small"
                    className={classes.strikedDataDesktop}
                  >
                    {returnDataWithUnit(defaultData)}
                  </Typography>
                )}

                <Typography
                  component="span"
                  variant="Kollektif/ExtraLarge"
                  className={classes.dataValue}
                >
                  {returnDataWithUnit(data)}
                </Typography>

                {hasDataChanged && (
                  <Typography
                    component="span"
                    variant="Kollektif/Small"
                    className={classes.strikedDataMobile}
                  >
                    {returnDataWithUnit(defaultData)}
                  </Typography>
                )}
              </div>
              <Typography
                variant="Kollektif/Small"
                className={classes.text}
                dataTestId="data-types"
              >
                {renderDataTypes()}
              </Typography>
            </div>
          </>
        ) : (
          <>
            {!isDataOnly && (
              <div
                className={cn(
                  classes.symbol,
                  classes.add,
                  classes.hideTabletDown
                )}
              >
                +
              </div>
            )}
            <div
              className={cn(classes.data, {
                [classes.hideTabletDown]: isDataOnly,
              })}
            >
              <Typography component="h2" variant="Kollektif/ExtraLarge">
                Unlimited
              </Typography>
              <Typography variant="Kollektif/Small" className={classes.text}>
                {renderDataTypes()}
              </Typography>
            </div>
          </>
        )}
      </div>
      <div className={classes.bannerBottom}>
        <div>
          <Typography
            component="h3"
            variant="Kollektif/MediumEmphasis"
            className={classes.statementTitle}
            dataTestId="plan-statement"
          >
            {planStatement}
          </Typography>
          <Typography
            variant="Kollektif/Small"
            dataTestId="plan-statement-text"
          >
            {planMeta.data_discount ? (
              isPlanDetails ? (
                <a
                  href="#expDiscount"
                  data-item-category="PP"
                  data-item-name="moneyback-more-info"
                >
                  {`Up to ${planMeta.data_discount} back every month`}
                </a>
              ) : (
                `Up to ${planMeta.data_discount} per month`
              )
            ) : (
              '1 month plan, cancel anytime. SIM and eSIM choices'
            )}
          </Typography>
        </div>
        <div className={classes.buttonWrapper}>
          <ColorButton
            ariaLabel={`Get the ${planMeta.name} plan`}
            href={`/order/${planMeta.slug}`}
            color={planMeta.color}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            data-testid="get-plan-button"
            data-item-category={TRACKING_CATEGORIES.NAVIGATION}
            data-item-name="get-plan-top-cta"
            onClick={() => {
              const product = buildAssetsProductsProps({ planMeta, priceData })
                .products[0]
              if (product?.id && product?.name) {
                triggerProductEvent(
                  DATA_LAYER_EVENTS.ADD_TO_CART,
                  product as { id: string; name: string; category: string },
                  CHECKOUT_TYPES.REGULAR,
                  SIM_JOURNEY_TYPES.ONLINE_SIM_ORDER_JOURNEY
                )
              }
            }}
          >
            {buyBtnTitle}
          </ColorButton>
          {FEATURES.PAYPAL_EXPRESS && !isAuthenticated && (
            <div
              className={classes.styledPaypalButton}
              id="paypal-button"
              data-item-category={TRACKING_CATEGORIES.NAVIGATION}
              data-item-name="pay-pal-buy-now"
            />
          )}
          {FEATURES.APPLE_PAY_PLAN_BANNER_BUTTON && !isAuthenticated && (
            <ApplePayButton
              label={`${priceData?.name} SIM plan for ${pricePresenter(
                price
              )} per month`}
              amount={Number(price.slice(1))}
            />
          )}
          {FEATURES.GOOGLE_PAY_PLAN_BANNER_BUTTON && !isAuthenticated && (
            <div
              className={classes.btn}
              id="google-pay-button"
              data-price={price.slice(1)}
            >
              Google Pay
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PlanDetailsBanner
